import { useState } from 'react';
import type { WatchlistKind } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { RatingStars } from '../components/RatingStars.js';
import { useRatings } from '../session/useRatings.js';
import { useWatchlist } from '../session/useWatchlist.js';

/**
 * What this device kept: films put aside for later, and films the group
 * actually settled on.
 *
 * Two lists rather than one screen per kind — they answer the same question
 * at different times ("what could we watch" / "what did we watch"), and
 * splitting them across screens would hide half the answer.
 */
export function WatchlistScreen({ onBack }: { onBack: () => void }) {
  const t = useT();
  const { entries, loading } = useWatchlist();
  const [kind, setKind] = useState<WatchlistKind>('saved');

  const shown = entries.filter((entry) => entry.kind === kind);
  const ratings = useRatings(shown.map((entry) => entry.movieId));

  return (
    <div className="screen">
      <div className="row row--between">
        <h1>{t('watchlist.title')}</h1>
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          {t('common.back')}
        </button>
      </div>

      <div className="chips">
        {(['saved', 'watched'] as const).map((option) => (
          <button
            key={option}
            type="button"
            className={`chip${kind === option ? ' chip--selected' : ''}`}
            aria-pressed={kind === option}
            onClick={() => setKind(option)}
          >
            {option === 'saved' ? t('watchlist.tabSaved') : t('watchlist.tabWatched')}
          </button>
        ))}
      </div>

      {loading ? <p className="muted">{t('common.loading')}</p> : null}

      {!loading && shown.length === 0 ? (
        <p className="muted">
          {kind === 'saved' ? t('watchlist.emptySaved') : t('watchlist.emptyWatched')}
        </p>
      ) : null}

      <ul className="watchlist">
        {shown.map((entry) => (
          <li key={`${entry.kind}:${entry.movieId}`} className="watchlist__item">
            <div className="row row--between">
              <span className="watchlist__title">{entry.title}</span>
              <time className="muted small" dateTime={entry.addedAt}>
                {new Date(entry.addedAt).toLocaleDateString()}
              </time>
            </div>
            {/* Rating belongs on the films you actually saw. Asking "how was
                it?" about something still on the shortlist is a question
                nobody can answer yet. */}
            {kind === 'watched' ? (
              <>
                <span className="small muted">{t('rating.prompt')}</span>
                <RatingStars
                  rating={ratings.ratings[entry.movieId] ?? null}
                  onRate={(score) => void ratings.rate(entry.movieId, score)}
                  onClear={() => void ratings.clear(entry.movieId)}
                />
              </>
            ) : null}
          </li>
        ))}
      </ul>

      {/* An anonymous cookie is the identity here, and saying so is fairer
          than letting somebody discover it when they switch devices. */}
      <p className="muted small">{t('watchlist.deviceNote')}</p>
    </div>
  );
}
